import { colorBaseGreyWarm100, colorBaseGreyWarm600, ProductParagraph } from '@issuu/silkscreen';
import type { Callbacks, TargetingArray } from '@issuu/widget-ads';
import { create as adsCreate } from '@issuu/widget-ads';
import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { maxSize } from '../../util/ads-utils';
import DocumentContext from '../DocumentPage/DocumentContext';

export const GoogleAdRoot = styled.div<{ width: number; height: number }>`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    color: ${colorBaseGreyWarm600};
    background-color: ${colorBaseGreyWarm100};
    width: ${props => props.width}px;
    height: ${props => props.height}px;
`;

const AbsoluteCenter = styled.div`
    position: absolute;
`;

export type Props = {
    adSizes: googletag.MultiSize;
    network: string;
    idSuffix: string;
    isShowInnerTitle?: boolean;
    callbacks?: Partial<Callbacks>;
};

export const GoogleAdsTemplate: React.FC<Props> = ({
    adSizes: sizes,
    idSuffix,
    network,
    isShowInnerTitle,
    callbacks,
}: Props) => {
    const {
        hosts: { pingback },
        visitor: { isLoggedIn },
        document: { originalPublishDateInISOString, pageCount, publicationId },
        iabCategories,
        publisher,
        enviroment,
    } = useContext(DocumentContext);

    const adRef = useRef<HTMLDivElement>(null);
    const [isAdsRequested, setIsAdsRequested] = useState<boolean>(false);
    const [isAdsFilled, setIsAdsFilled] = useState<boolean>(false);
    // Targetings are defined in Google Ads Manger by ISSUU AdOps.
    // Developer needs to get an agreement with AdOps and update key and value at the same time.
    // In legacy document page they were defined at here :
    // https://github.com/issuu/legacy-document-page/blob/f56dadb6c0d8c39c0fb3d37cc6184109c31d25df/client/src/modules/Ads/Placement.js#L217-L237
    const adTargeting: TargetingArray[] = [
        ['iabInterestIDs', iabCategories],
        ['isLoggedIn', isLoggedIn.toString()],
        ['uploadTimestamp', originalPublishDateInISOString.slice(0, 10)],
        ['publicationPageCount', pageCount.toString()],
        ['publicationId', publicationId],
        ['isDevelopmentTest', String(enviroment === 'dev')],
        ['userId', publisher.userId.toString()],
        ['publisherPlan', publisher.userPlan],
        ['adConnect', publisher.licenses.docpageAds ? 'yes' : 'no'],
        ['randomBucket', Math.floor(Math.random() * 10 + 1).toString()],
        ['pageType', 'docPage'],
        ['newDocPage', 'true'],
        ['experiment', 'control'],
    ];

    const id = `${network.replace(/\/\d+\//, '')}__${idSuffix}`;

    useEffect(() => {
        if (!adRef.current || isAdsRequested) {
            return;
        }
        const ads = adsCreate({
            rootElm: adRef.current,
            network,
            sizes,
            tracking: {
                pingbackHost: pingback,
                trackingKey: id,
            },
            callbacks: {
                ...callbacks,
                onAdFilled: () => {
                    callbacks?.onAdFilled && callbacks?.onAdFilled();
                    setIsAdsFilled(true);
                },
            },
            adTargeting,
            adProvider: ['zdissuu', 'zhengdai', 'issuu'].includes(publisher.username)
                ? 'htlbid'
                : 'googletag',
        }).show();

        setIsAdsRequested(true);

        return () => {
            setIsAdsRequested(false);
            ads.destroySlot();
        };
    }, []);

    const [width, height] = maxSize(sizes);

    return (
        <GoogleAdRoot height={height} width={width}>
            {isShowInnerTitle && !isAdsFilled ? (
                <AbsoluteCenter>
                    <ProductParagraph step="negative-2">Advertisement</ProductParagraph>
                </AbsoluteCenter>
            ) : null}
            <div id={id} ref={adRef} />
        </GoogleAdRoot>
    );
};
